/**
 * TeamDeleteSection: the staff delete of a team (TRA-1508), the same
 * delete the owner has on Settings > Teams, so a test team can be reset
 * without SQL or kubectl. The dialog shows what goes and why the api
 * would refuse (a running Job, a running training run, a protected team)
 * before the name is typed. The api checks the name too. After the delete
 * the staff member lands on the team list, which is refetched.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import {
  useDeleteTeamApiInternalTeamsTeamIdDelete,
  useGetTeamDeletePreviewApiInternalTeamsTeamIdDeletePreviewGet,
} from '@/api/generated/internal/internal'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { TeamDeleteDialog, type TeamDeletePreviewState } from '@/components/shared/TeamDeleteDialog'
import { Button } from '@/components/ui/button'
import { INTERNAL_TEAMS_QUERY_KEY } from '@/hooks/useInternalTeams'

import { EMPLOYEES_ROOT } from './paths'
import type { TeamConfigSectionProps } from './sections'

export const DELETE_SECTION_DESCRIPTION =
  'Remove the team, its conversations and usage records, its data in the database and the bucket, its gateway team and API keys, its Jobs and its login group.'

function useStaffDeletePreview(teamId: string, open: boolean): TeamDeletePreviewState {
  const query = useGetTeamDeletePreviewApiInternalTeamsTeamIdDeletePreviewGet(teamId, {
    query: { enabled: open, retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.isError ? (getApiErrorDetail(query.error) ?? 'The preview failed to load.') : null,
  }
}

export function TeamDeleteSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const preview = useStaffDeletePreview(team.id, open)
  const deleteMutation = useDeleteTeamApiInternalTeamsTeamIdDelete()

  const handleDelete = (confirm: string): void => {
    setError(null)
    deleteMutation.mutate(
      { teamId: team.id, params: { confirm } },
      {
        onSuccess: () => {
          toast.success(`${team.name} deleted`)
          void queryClient.invalidateQueries({ queryKey: INTERNAL_TEAMS_QUERY_KEY })
          navigate(EMPLOYEES_ROOT)
        },
        onError: (failure) => {
          setError(getApiErrorDetail(failure) ?? 'Failed to delete team. Please try again.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection title="Delete team" description={DELETE_SECTION_DESCRIPTION}>
        <Button
          variant="destructive"
          onClick={() => {
            setError(null)
            setOpen(true)
          }}
        >
          Delete {team.name}
        </Button>
      </SettingsSection>
      <TeamDeleteDialog
        open={open}
        onOpenChange={setOpen}
        teamName={team.name}
        preview={preview}
        isPending={deleteMutation.isPending}
        error={error}
        onConfirm={handleDelete}
      />
    </div>
  )
}
